/*
 * Players Page — Conversion-focused landing for competitive players
 * Skill-based earning, game categories, earnings potential, success stories
 */

import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { motion } from "framer-motion";
import {
  Trophy, DollarSign, ArrowRight, CheckCircle2, Gamepad2,
  ChefHat, Dumbbell, Palette, Music, Code, Camera, Brain,
  Mic, Swords, Star, Shield, TrendingUp, Users, Zap,
  Play, Clock, Wallet, Award, Target,
} from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import { useState, useMemo } from "react";

const gameCategories = [
  { icon: Gamepad2, name: "Esports", desc: "Fortnite, Valorant, CS2, League of Legends, Rocket League", example: "$10 entry, 200 players, $1,400 to the winner" },
  { icon: ChefHat, name: "Cooking", desc: "Recipe challenges, bake-offs, plating competitions, mystery ingredient rounds", example: "$5 entry, 500 fans compete, $1,750 first prize" },
  { icon: Dumbbell, name: "Fitness", desc: "Transformation challenges, workout battles, endurance tests, CrossFit WODs", example: "$20 entry, 100 athletes, $1,400 champion prize" },
  { icon: Palette, name: "Art & Design", desc: "Digital art, illustration, logo design, photography, graphic design battles", example: "$15 entry, 80 artists, $840 to the best work" },
  { icon: Music, name: "Music", desc: "Beat making, songwriting, DJ sets, cover songs, freestyle battles", example: "$10 entry, 150 producers, $1,050 winner" },
  { icon: Code, name: "Coding", desc: "Hackathons, algorithm challenges, app building, bug bounties", example: "$25 entry, 200 devs, $3,500 grand prize" },
  { icon: Camera, name: "Photography", desc: "Landscape, portrait, street, wildlife, themed photo challenges", example: "$5 entry, 1,000 photographers, $3,500 winner" },
  { icon: Brain, name: "Trivia & Knowledge", desc: "General knowledge, niche topics, speed rounds, team trivia", example: "$3 entry, 500 players, $1,050 to the brainiest" },
  { icon: Mic, name: "Content Creation", desc: "Best TikTok, YouTube short, podcast clip, meme competition", example: "$10 entry, 300 creators, $2,100 top prize" },
  { icon: Swords, name: "Strategy", desc: "Chess, poker (skill-based), fantasy sports, prediction markets", example: "$20 entry, 64 players, $896 to the strategist" },
];

const successStories = [
  {
    name: "Maria G.",
    location: "Mexico City, Mexico",
    game: "Culinary Clash: Street Food Edition",
    won: "$2,500",
    quote: "I entered a $5 cooking competition with my grandmother's tamale recipe. 500 people entered. I won first place and used the money to start my food truck.",
  },
  {
    name: "David K.",
    location: "Seoul, South Korea",
    game: "Code Sprint: React Challenge",
    won: "$3,200",
    quote: "As a junior developer, I entered a coding competition to test my skills. I built the best React app in 48 hours and won enough to cover three months of rent.",
  },
  {
    name: "Amara O.",
    location: "Lagos, Nigeria",
    game: "Digital Art Showdown",
    won: "$1,800",
    quote: "I've been doing digital art for years but never earned from it. GamePoint let me compete against other artists worldwide. My Afrofuturism piece won and changed my career.",
  },
  {
    name: "Jake T.",
    location: "Austin, Texas",
    game: "Fitness Transformation Battle",
    won: "$1,400",
    quote: "I was already working out every day. Why not get paid for it? Entered a 30-day transformation challenge, documented my progress, and the community voted me the winner.",
  },
];

const howItWorks = [
  { step: "1", icon: Target, title: "Find Your Game", desc: "Browse competitions in your skill area — cooking, gaming, fitness, art, coding, music, trivia, and dozens more. Filter by entry fee, prize pool, and deadline." },
  { step: "2", icon: DollarSign, title: "Pay a Small Entry Fee", desc: "Entry fees start as low as $1. Your fee goes directly into the prize pool. No hidden charges. You see exactly where every dollar goes before you enter." },
  { step: "3", icon: Star, title: "Submit Your Entry", desc: "Upload your work — a photo, video, code project, recipe, workout log, or whatever the competition requires. Show the world what you can do." },
  { step: "4", icon: Trophy, title: "Win & Cash Out", desc: "Winners are determined by community votes, judge panels, or objective scoring — never by luck. Cash out your winnings instantly to your wallet." },
];

export default function Players() {
  const [entryFee, setEntryFee] = useState(10);
  const [playerCount, setPlayerCount] = useState(200);

  const earnings = useMemo(() => {
    const total = entryFee * playerCount;
    // Platform (15%) + default host/promoter cuts (10% + 10%) leave 65%
    // for the prize pool — the same floor every real game guarantees.
    const prize = total * 0.65;
    const first = Math.round(prize * 0.5);
    const second = Math.round(prize * 0.3);
    const third = Math.round(prize * 0.2);
    return { total, prize, first, second, third };
  }, [entryFee, playerCount]);

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-24 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-teal/5 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-neon/10 border border-neon/20 text-neon text-xs font-medium mb-6">
              <Trophy className="w-3.5 h-3.5" />
              For Players
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.08] tracking-tight mb-6">
              Your Skills Are Worth{" "}
              <span className="text-gradient-neon">Real Money</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl">
              Stop playing for free. Enter skill-based competitions in cooking, gaming, fitness,
              art, coding, music, trivia, and more. Low entry fees. Massive prize pools.
              Real payouts. No luck involved — just your talent against the world.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/games">
                <Button size="lg" className="bg-neon hover:bg-neon-light text-background font-semibold gap-2 h-12 px-8">
                  <Play className="w-5 h-5" />
                  Browse Live Games
                </Button>
              </Link>
              <Link href="/create">
                <Button size="lg" variant="outline" className="h-12 px-8 border-border/50 hover:border-neon/50 hover:text-neon gap-2">
                  Or Create Your Own
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Play on GamePoint */}
      <section className="py-20 px-6 bg-secondary/20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Why <span className="text-gradient-neon">Players</span> Choose GamePoint
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            This isn't gambling. This isn't luck. This is skill-based competition where
            the best work wins — and everyone can see exactly where the money goes.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "100% Skill-Based", desc: "Winners are determined by talent, creativity, and effort. Judges, community votes, or objective metrics — never random chance." },
              { icon: DollarSign, title: "Transparent Payouts", desc: "See the full prize breakdown before you enter. Entry fees, host cut, promoter cut, and prize pool — all visible upfront." },
              { icon: Clock, title: "Fast Cycles", desc: "Most competitions run 1-7 days. Enter today, compete this week, cash out by Friday. No months-long waits." },
              { icon: Wallet, title: "Instant Withdrawals", desc: "Win money? Cash it out immediately. PayPal, bank transfer, or crypto. Your earnings, your way, on your schedule." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-panel rounded-xl p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-neon/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-neon" />
                </div>
                <h3 className="text-base font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Game Categories */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Compete in <span className="text-gradient-gold">Any Category</span>
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Whatever you're good at, there's a competition for it. From esports to cooking,
            photography to coding — your skills have value.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {gameCategories.map((cat, i) => (
              <motion.div
                key={cat.name}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="glass-panel rounded-xl p-5 hover:border-neon/30 transition-all group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-neon/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <cat.icon className="w-5 h-5 text-neon" />
                  </div>
                  <h3 className="font-semibold">{cat.name}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{cat.desc}</p>
                <p className="text-xs text-neon font-mono">{cat.example}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Calculator */}
      <section className="py-24 px-6 bg-secondary/20">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            <span className="text-gradient-neon">Earnings</span> Calculator
          </h2>
          <p className="text-muted-foreground text-center max-w-xl mx-auto mb-12">
            See what you could win. Adjust the entry fee and player count to see real prize pools.
          </p>

          <div className="glass-panel rounded-2xl p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Entry Fee</label>
                  <div className="flex items-center gap-3">
                    <span className="text-neon font-bold text-lg">$</span>
                    <input
                      type="number"
                      min={1}
                      max={500}
                      value={entryFee}
                      onChange={(e) => setEntryFee(Math.max(1, Number(e.target.value)))}
                      className="w-full bg-background border border-border rounded-lg px-4 py-2 text-lg font-mono"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Number of Players</label>
                  <input
                    type="number"
                    min={2}
                    max={10000}
                    value={playerCount}
                    onChange={(e) => setPlayerCount(Math.max(2, Number(e.target.value)))}
                    className="w-full bg-background border border-border rounded-lg px-4 py-2 text-lg font-mono"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  * Assumes 10% host cut and 10% promoter cut. Actual splits vary by game.
                </p>
              </div>

              <div className="space-y-4">
                <div className="glass-panel rounded-xl p-4 border border-neon/20">
                  <p className="text-sm text-muted-foreground">Total Prize Pool</p>
                  <p className="text-3xl font-bold font-mono text-gradient-neon">${earnings.prize.toLocaleString()}</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="glass-panel rounded-xl p-3 text-center border border-gold/20">
                    <Trophy className="w-5 h-5 text-gold mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">1st Place</p>
                    <p className="text-lg font-bold font-mono text-gold">${earnings.first.toLocaleString()}</p>
                  </div>
                  <div className="glass-panel rounded-xl p-3 text-center">
                    <Award className="w-5 h-5 text-slate-300 mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">2nd Place</p>
                    <p className="text-lg font-bold font-mono">${earnings.second.toLocaleString()}</p>
                  </div>
                  <div className="glass-panel rounded-xl p-3 text-center">
                    <Award className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">3rd Place</p>
                    <p className="text-lg font-bold font-mono">${earnings.third.toLocaleString()}</p>
                  </div>
                </div>
                <p className="text-center text-sm text-muted-foreground">
                  Your entry: <span className="text-neon font-mono font-bold">${entryFee}</span> — potential return: <span className="text-gold font-mono font-bold">{Math.round(earnings.first / entryFee)}x</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            How <span className="text-gradient-neon">Players</span> Earn
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-neon/10 border border-neon/20 flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-6 h-6 text-neon" />
                </div>
                <h3 className="text-base font-bold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories — illustrative examples of how the platform works */}
      <section className="py-24 px-6 bg-secondary/20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            How Players <span className="text-gradient-gold">Win on GamePoint</span>
          </h2>
          <p className="text-muted-foreground text-center max-w-xl mx-auto mb-2">
            Example journeys showing what competing — and winning — can look like.
          </p>
          <p className="text-xs text-muted-foreground/70 text-center max-w-xl mx-auto mb-12 italic">
            Illustrative examples. Names and details are fictional; prize amounts depend on each game's entry fees and pool.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {successStories.map((story, i) => (
              <motion.div
                key={story.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-panel rounded-xl p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-neon/20 flex items-center justify-center text-sm font-bold text-neon">
                      {story.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold">{story.name}</p>
                      <p className="text-xs text-muted-foreground">{story.location}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gold">{story.won}</p>
                    <p className="text-xs text-muted-foreground">Won</p>
                  </div>
                </div>
                <p className="text-xs text-neon font-medium mb-2">{story.game}</p>
                <p className="text-sm text-muted-foreground leading-relaxed italic">"{story.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Bonus */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Earn Even When You <span className="text-gradient-neon">Don't Play</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Every player gets a referral code. Share it with friends and earn <span className="text-neon font-bold">$25</span> when
            they make their first deposit, plus <span className="text-gold font-bold">5% of their winnings for life</span>.
            Your friend wins $1,000? You get $50. They win again? You get paid again. Forever.
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-8">
            <div className="glass-panel rounded-xl p-4">
              <p className="text-2xl font-bold text-neon">$25</p>
              <p className="text-xs text-muted-foreground">Per referral signup</p>
            </div>
            <div className="glass-panel rounded-xl p-4">
              <p className="text-2xl font-bold text-gold">5%</p>
              <p className="text-xs text-muted-foreground">Of their winnings</p>
            </div>
            <div className="glass-panel rounded-xl p-4">
              <p className="text-2xl font-bold text-neon">Forever</p>
              <p className="text-xs text-muted-foreground">Lifetime earnings</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 bg-secondary/20">
        <div className="max-w-3xl mx-auto text-center glass-panel rounded-2xl p-12 glow-neon">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Your Next Win Is <span className="text-gradient-neon">One Click Away</span>
          </h2>
          <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
            Hundreds of live competitions are waiting for you right now. Pick your skill,
            pay a small entry fee, and show the world what you've got.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/games">
              <Button size="lg" className="bg-neon hover:bg-neon-light text-background font-semibold gap-2 h-12 px-8">
                <Play className="w-5 h-5" />
                Browse Live Games
              </Button>
            </Link>
            <Link href="/create">
              <Button size="lg" variant="outline" className="h-12 px-8 border-neon/30 hover:border-neon text-neon gap-2">
                <Zap className="w-5 h-5" />
                Create Your Own
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
